import { useState } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d72ma44ij602egd95l50';

interface FormState {
  name: string;
  email: string;
  phone: string;
  service: string;
  postcode: string;
  message: string;
}

const initialForm: FormState = {
  name: '', email: '', phone: '', service: '', postcode: '', message: '',
};

export default function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      (Object.keys(form) as (keyof FormState)[]).forEach((k) => body.append(k, form[k]));
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm(initialForm);
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/13e636bcac4c0a9755fa5d4388fbde2c.jpg"
          alt="BritFit service background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left — Info */}
          <div className="pt-4">
            <span className="inline-block border border-white/20 text-white/60 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              Get In Touch
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
              Ready to Get<br />
              Your Tyres Sorted?
            </h2>
            <p className="text-stone-300 text-sm leading-relaxed mb-10">
              Fill in the form and one of our team will be in touch within the hour. For urgent callouts, call us directly — we&apos;re available 24/7.
            </p>

            {/* Contact cards */}
            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-phone-line', label: 'Call Us', value: '+44 1234 567890', sub: 'Available 24/7' },
                { icon: 'ri-mail-line', label: 'Email', value: 'hello@britfit.co.uk', sub: 'We reply within 1 hour' },
                { icon: 'ri-map-pin-2-line', label: 'Coverage', value: 'England, Scotland & Wales', sub: 'Nationwide mobile service' },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                  <div className="w-10 h-10 flex-none flex items-center justify-center bg-[#1B4332] rounded-xl">
                    <i className={`${c.icon} text-white text-base`} />
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">{c.label}</div>
                    <div className="text-white text-sm font-semibold">{c.value}</div>
                    <div className="text-green-300 text-xs">{c.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <h3 className="text-stone-900 font-bold text-lg mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
              Book a Fitting or Enquire
            </h3>
            <p className="text-stone-400 text-xs mb-6">All fields marked * are required</p>

            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 flex items-center justify-center bg-[#E8F5EE] rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-[#1B4332] text-2xl" />
                </div>
                <div className="text-stone-900 font-bold text-base mb-2">Message Sent!</div>
                <p className="text-stone-400 text-sm">We&apos;ll be in touch very shortly. For urgent help, call us directly.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-[#1B4332] text-sm font-semibold cursor-pointer underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-600 text-xs font-medium mb-1.5 block">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-stone-600 text-xs font-medium mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-600 text-xs font-medium mb-1.5 block">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+44 7700 000000"
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-stone-600 text-xs font-medium mb-1.5 block">Postcode *</label>
                    <input
                      type="text"
                      name="postcode"
                      value={form.postcode}
                      onChange={handleChange}
                      required
                      placeholder="e.g. M1 1AA"
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1.5 block">Service Required</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors cursor-pointer bg-white"
                  >
                    <option value="">Select a service...</option>
                    <option value="Mobile Tyre Fitting">Mobile Tyre Fitting</option>
                    <option value="Puncture Repair">Puncture Repair</option>
                    <option value="Wheel Balancing">Wheel Balancing</option>
                    <option value="TPMS Services">TPMS Services</option>
                    <option value="General Enquiry">General Enquiry</option>
                  </select>
                </div>

                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1.5 block">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us more about what you need..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-[#1B4332] transition-colors resize-none"
                  />
                  <div className={`text-xs text-right mt-1 ${charCount > 500 ? 'text-red-500' : 'text-stone-400'}`}>
                    {charCount}/500
                  </div>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-xs">Something went wrong. Please try again or call us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="w-full bg-[#1B4332] hover:bg-[#145228] disabled:opacity-60 text-white font-semibold py-3 rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
